import { useCallback, useRef, useState } from "react";
import { useTenant } from "./TenantProvider.jsx";
import { useFilters } from "../lib/useFilters.js";
import { ALL_TENANTS } from "../../shared/tenants.js";
import { fmtCompact } from "../lib/format.js";
import { Icon, popoverClass, useDismiss } from "../components/ui/index.js";

/**
 * Sidebar tenant switcher: every report, filter and table re-scopes to the chosen tenant.
 * `variant="sidebar"` draws the trigger as a soft panel inside the sidebar.
 */
export function TenantSwitcher({ variant }) {
  const { tenants, current, allowAll } = useTenant();
  const { setTenant } = useFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useDismiss(ref, open, useCallback(() => setOpen(false), []));

  const options = [...(allowAll ? [{ key: ALL_TENANTS, name: "All tenants", users30d: null }] : []), ...tenants];

  return (
    <div ref={ref} className="relative">
      {variant === "sidebar" ? (
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-control border border-side-line bg-surface-2 px-3 py-2.5 text-left text-[14px] text-side-ink transition-colors hover:border-line-default hover:bg-surface-1"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Tenant: ${current.name}. Change tenant`}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="lock" className="size-4 text-side-ink-3" strokeWidth={1.4} />
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-semibold tracking-[0.12em] text-side-ink-3 uppercase">Tenant</span>
            <span className="block truncate font-medium">{current.name}</span>
          </span>
          <Icon name="chevronDown" className={`size-3.5 text-side-ink-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      ) : (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-control border border-line-default bg-surface-1 px-2.5 py-1.5 text-left text-[12px] hover:border-line-strong"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="min-w-0">
            <span className="block text-[9px] font-semibold tracking-[0.1em] text-ink-3 uppercase">Tenant</span>
            <span className="block truncate font-medium text-ink">{current.name}</span>
          </span>
          <Icon name="chevronDown" className="size-3.5 text-ink-3" />
        </button>
      )}
      {open && (
        <ul role="listbox" className={`scroll-thin absolute top-full left-0 mt-1.5 max-h-96 w-80 overflow-auto p-1 ${popoverClass}`}>
          {options.map((t) => {
            const on = t.key === current.key;
            return (
              <li key={t.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`flex w-full items-center justify-between gap-3 rounded-control px-3 py-2.5 text-left text-[14px] transition-colors ${on ? "bg-accent-soft text-accent-ink" : "text-ink-2 hover:bg-surface-2 hover:text-ink"}`}
                  onClick={() => {
                    setTenant(t.key);
                    setOpen(false);
                  }}
                >
                  <span className="flex min-w-0 items-center gap-2 font-medium">
                    {t.logo ? (
                      <img src={t.logo} alt="" className="size-5 shrink-0 rounded object-contain" loading="lazy" />
                    ) : (
                      <span aria-hidden className="grid size-5 shrink-0 place-items-center rounded bg-surface-2 text-[10px] font-semibold text-ink-3">
                        {t.name.slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span className="truncate">{t.name}</span>
                    {t.discovered && <span className="rounded-full bg-surface-2 px-1.5 text-[9px] text-ink-3 uppercase">new</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {t.users30d !== null && t.users30d !== undefined && (
                      <span className="tabular text-[12px] text-ink-3">{t.users30d ? `${fmtCompact(t.users30d)} · 30d` : "no activity"}</span>
                    )}
                    {on && <Icon name="check" className="size-3.5" />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
